import { RefObject, useEffect, useRef } from 'react';
import { ContentItem } from '../../../models/ContentManagementInterface';

/** A dialog opened from inside the panel renders in a portal outside it; interacting with it isn't "outside". */
const MODAL_SELECTOR = '.MuiModal-root';

interface Params {
  open: boolean;
  close: () => void;
  draggingType: ContentItem['type'] | null;
  panelRef: RefObject<HTMLElement | null>;
}

/**
 * Closes the sidebar on Escape, a click outside the panel, or once a dragged
 * block has been released. An open dialog (e.g. editing a video's details from
 * the Media tab) is left alone.
 */
export function usePaletteDismiss({ open, close, draggingType, panelRef }: Params): void {
  const prevDragging = useRef(draggingType);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector(MODAL_SELECTOR)) close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target.closest?.(MODAL_SELECTOR)) return;
      if (!panelRef.current?.contains(target)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close, panelRef]);

  useEffect(() => {
    if (prevDragging.current && !draggingType) close();
    prevDragging.current = draggingType;
  }, [draggingType, close]);
}
